import { useCallback, useMemo, useState } from "react";

import type { ResourceType } from "../store/api/resourceTypes";

export interface BookmarkItem {
  id: string;
}

export interface UseResourceBookmarksOptions {
  /**
   * Identifies the kind(s) of resource being bookmarked. Pass an array for a
   * picker that searches multiple resource types at once (e.g. "all
   * resources") — order doesn't matter, it's normalized before use.
   */
  resourceType: ResourceType | readonly ResourceType[];
  /**
   * Identifies which picker instance owns this list. Pickers sharing the same
   * `resourceType` + `cacheId` share one starred/recents list; different
   * `cacheId`s get isolated lists.
   */
  cacheId: string;
  /** Rows shown in the default (empty-query) view before recents run out. Default 5. */
  maxDefaultSlots?: number;
  /** How many past selections to retain, so recents can still backfill display
   * slots after excluding anything already starred. Default 15. */
  recentsBufferSize?: number;
}

interface StoredState<T> {
  /** Oldest-starred-first. */
  starred: T[];
  /** Most-recent-first. */
  recents: T[];
}

const EMPTY_STATE: StoredState<never> = { starred: [], recents: [] };

/**
 * Shared `cacheId`s so every picker for a given resource type contributes to
 * and reads from the same starred/recents list, regardless of where it's
 * used (e.g. data mounts selector, new notebook dataset picker, data
 * explorer home table all share "data"). Pass a different cacheId at a call
 * site if that picker should ever need its own isolated list instead.
 */
export const SHARED_BOOKMARK_CACHE_IDS = {
  data: "data",
  account: "account",
  /** Shared by any selector combining the "user" and "role" resource types. */
  userOrRole: "user-role",
} as const;

function storageKeyFor(
  resourceType: ResourceType | readonly ResourceType[],
  cacheId: string,
) {
  const resourceTypeKey = Array.isArray(resourceType)
    ? resourceType.slice().sort().join(",")
    : resourceType;
  return `resourceSelector:${resourceTypeKey}:${cacheId}`;
}

function readStorage<T>(key: string): StoredState<T> {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw);
    return {
      starred: Array.isArray(parsed.starred) ? parsed.starred : [],
      recents: Array.isArray(parsed.recents) ? parsed.recents : [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

/**
 * Manages a starred + recents list for a resource picker, persisted to
 * localStorage. Item snapshots (not just IDs) are stored so the default view
 * can render immediately without re-fetching by ID; labels may go stale if
 * the underlying resource is renamed, which is an acceptable tradeoff for a
 * quick-access shortcut.
 */
export function useResourceBookmarks<T extends BookmarkItem>({
  resourceType,
  cacheId,
  maxDefaultSlots = 5,
  recentsBufferSize = 15,
}: UseResourceBookmarksOptions) {
  const storageKey = storageKeyFor(resourceType, cacheId);
  const [state, setState] = useState<StoredState<T>>(() =>
    readStorage<T>(storageKey),
  );

  const persist = useCallback(
    (next: StoredState<T>) => {
      localStorage.setItem(storageKey, JSON.stringify(next));
      setState(next);
    },
    [storageKey],
  );

  const isStarred = useCallback(
    (id: string) => state.starred.some((item) => item.id === id),
    [state.starred],
  );

  const toggleStar = useCallback(
    (item: T) => {
      const alreadyStarred = state.starred.some((s) => s.id === item.id);
      // Newly starred items are appended, keeping the list oldest-starred-first.
      const starred = alreadyStarred
        ? state.starred.filter((s) => s.id !== item.id)
        : [...state.starred, item];
      persist({ ...state, starred });
    },
    [state, persist],
  );

  const recordRecent = useCallback(
    (item: T) => {
      const recents = [
        item,
        ...state.recents.filter((r) => r.id !== item.id),
      ].slice(0, recentsBufferSize);
      persist({ ...state, recents });
    },
    [state, persist, recentsBufferSize],
  );

  /**
   * One-time seed from legacy storage, skipped once recents exist. Checked
   * independent of `starred` since another picker may star first. Return
   * value lets callers clean up the legacy source only once migrated.
   */
  const seedRecentsIfEmpty = useCallback(
    (items: T[]) => {
      if (state.recents.length > 0) return false;
      if (items.length === 0) return false;
      persist({ ...state, recents: items.slice(0, recentsBufferSize) });
      return true;
    },
    [state, persist, recentsBufferSize],
  );

  const defaultItems = useMemo(() => {
    const starredIds = new Set(state.starred.map((s) => s.id));
    const backfill = state.recents.filter((r) => !starredIds.has(r.id));
    const slotsForRecents = Math.max(maxDefaultSlots - state.starred.length, 0);
    return [...state.starred, ...backfill.slice(0, slotsForRecents)];
  }, [state, maxDefaultSlots]);

  return useMemo(
    () => ({
      defaultItems,
      starredItems: state.starred,
      /** Full recents list, uncapped by `maxDefaultSlots` — for callers (like
       * search-mode matching) that need every recent, not just the default
       * view's display slice. */
      recentItems: state.recents,
      isStarred,
      toggleStar,
      recordRecent,
      seedRecentsIfEmpty,
    }),
    [
      defaultItems,
      state.starred,
      state.recents,
      isStarred,
      toggleStar,
      recordRecent,
      seedRecentsIfEmpty,
    ],
  );
}
